/**
 * Product page: option buttons, picture gallery and zoom.
 *
 * - Swaps the plain variant dropdown for option buttons (Color, Size...).
 *   Picking options finds the matching variant and updates the price,
 *   sale/sold-out tags, the add button, the "(N in cart)" note, the
 *   address bar (?variant=) and the picture.
 * - Options that are sold out with the other current choices are crossed
 *   out but can still be picked, so people can see what exists.
 * - Thumbnails scroll the gallery; tapping a picture opens it full size.
 */
(() => {
  const product = document.querySelector('[data-product]');
  if (!product) return;

  const form = product.querySelector('form[action*="/cart/add"]');
  const variantsEl = product.querySelector('[data-variants]');
  const variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
  const idInput = product.querySelector('[data-variant-select]');
  const optionsBox = product.querySelector('[data-options]');
  const addButton = product.querySelector('[data-add-button]');
  const priceBox = product.querySelector('[data-product-price]');
  const inCartEl = product.querySelector('[data-in-cart]');
  const track = product.querySelector('[data-gallery-track]');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Gallery ---------- */

  const slides = track ? [...track.querySelectorAll('[data-media-id]')] : [];
  const thumbs = [...product.querySelectorAll('[data-thumb]')];

  const markThumb = (id) => {
    thumbs.forEach((t) => {
      if (t.dataset.thumb === String(id)) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  };

  const showMedia = (id, smooth = true) => {
    const slide = slides.find((s) => s.dataset.mediaId === String(id));
    if (!slide || !track) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
    markThumb(id);
  };

  thumbs.forEach((t) => t.addEventListener('click', () => showMedia(t.dataset.thumb)));

  // Keep the thumbnails in step when someone swipes the gallery.
  if (track && 'IntersectionObserver' in window && thumbs.length) {
    const seen = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) markThumb(e.target.dataset.mediaId);
        });
      },
      { root: track, threshold: 0.6 }
    );
    slides.forEach((s) => seen.observe(s));
  }

  const initial = track && track.querySelector('[data-initial]');
  if (initial && initial !== slides[0]) showMedia(initial.dataset.mediaId, false);

  /* ---------- Zoom ---------- */

  const lightbox = document.querySelector('[data-lightbox]');
  const zooms = [...product.querySelectorAll('[data-zoom]')];
  let zoomAt = 0;

  const showZoom = (index) => {
    zoomAt = (index + zooms.length) % zooms.length;
    const img = lightbox.querySelector('[data-lightbox-img]');
    img.src = zooms[zoomAt].dataset.zoom;
    img.alt = zooms[zoomAt].dataset.zoomAlt || '';
  };

  if (lightbox) {
    zooms.forEach((btn, i) =>
      btn.addEventListener('click', () => {
        showZoom(i);
        lightbox.showModal();
      })
    );
    lightbox.addEventListener('click', (event) => {
      const step = event.target.closest('[data-lightbox-step]');
      if (step) showZoom(zoomAt + Number(step.dataset.lightboxStep));
      else if (event.target.matches('[data-lightbox-img]')) lightbox.close();
    });
    lightbox.addEventListener('keydown', (event) => {
      if (zooms.length < 2) return;
      if (event.key === 'ArrowRight') showZoom(zoomAt + 1);
      if (event.key === 'ArrowLeft') showZoom(zoomAt - 1);
    });
  }

  /* ---------- Options ---------- */

  if (!form || !idInput || variants.length === 0) return;

  let current = variants.find((v) => String(v.id) === String(idInput.value)) || variants[0];

  const updateInCart = () => {
    if (!inCartEl) return;
    const n = current ? current.inCart : 0;
    inCartEl.textContent = n > 0 ? '(' + inCartEl.dataset.template.replace('#', String(n)) + ')' : '';
  };

  const updatePrice = () => {
    if (!priceBox || !current) return;
    const wrap = document.createElement('span');
    wrap.className = 'price' + (current.compare ? ' price--sale' : '');
    const now = document.createElement('span');
    now.className = 'price__now';
    now.textContent = current.price;
    if (current.compare) {
      const saleLabel = document.createElement('span');
      saleLabel.className = 'sr-only';
      saleLabel.textContent = priceBox.dataset.saleLabel;
      const regularLabel = document.createElement('span');
      regularLabel.className = 'sr-only';
      regularLabel.textContent = priceBox.dataset.regularLabel;
      const was = document.createElement('s');
      was.className = 'price__was';
      was.textContent = current.compare;
      wrap.append(saleLabel, now, ' ', regularLabel, was);
    } else {
      wrap.append(now);
    }
    priceBox.replaceChildren(wrap);
  };

  const updateButton = () => {
    const available = current && current.available;
    let label = addButton.dataset.labelAdd;
    if (!current) label = addButton.dataset.labelUnavailable;
    else if (!available) label = addButton.dataset.labelSoldOut;
    addButton.textContent = label;
    addButton.disabled = !available;
    addButton.toggleAttribute('data-sold-out', !available);

    const soldOut = product.querySelector('[data-badge-soldout]');
    const sale = product.querySelector('[data-badge-sale]');
    if (soldOut) soldOut.hidden = !current || available;
    if (sale) sale.hidden = !(available && current.compare);
  };

  const fieldsets = optionsBox ? [...optionsBox.querySelectorAll('[data-option]')] : [];

  const chosen = () =>
    fieldsets.map((fs) => {
      const checked = fs.querySelector('input:checked');
      return checked ? checked.value : null;
    });

  // Cross out values that are sold out (or don't exist) with the other current choices.
  const markAvailability = (picked) => {
    fieldsets.forEach((fs, index) => {
      fs.querySelectorAll('input').forEach((input) => {
        const ok = variants.some(
          (v) => v.available && v.options[index] === input.value && v.options.every((o, i) => i === index || o === picked[i])
        );
        input.nextElementSibling.classList.toggle('is-unavailable', !ok);
      });
      const label = fs.querySelector('[data-option-chosen]');
      if (label) label.textContent = picked[index] || '';
    });
  };

  const apply = () => {
    const picked = chosen();
    current = variants.find((v) => v.options.every((o, i) => o === picked[i])) || null;
    markAvailability(picked);

    if (current) {
      idInput.value = current.id;
      const url = new URL(location.href);
      url.searchParams.set('variant', current.id);
      history.replaceState(history.state, '', url);
      if (current.media) showMedia(current.media);
    }
    updatePrice();
    updateButton();
    updateInCart();
  };

  if (optionsBox) {
    optionsBox.hidden = false;
    const field = product.querySelector('[data-variant-field]');
    if (field) field.hidden = true;
    markAvailability(chosen());
    optionsBox.addEventListener('change', apply);
  }

  // The option buttons are only for picking; keep them out of the cart request.
  form.addEventListener('formdata', (event) => {
    for (const key of [...event.formData.keys()]) {
      if (key.startsWith('option-')) event.formData.delete(key);
    }
  });

  // After adding to the cart (assets/theme.js), bump "(N in cart)".
  form.addEventListener('cart:added', (event) => {
    const v = variants.find((x) => x.id === event.detail.variantId);
    if (v && event.detail.item) {
      v.inCart = event.detail.cart.items
        .filter((i) => i.variant_id === v.id)
        .reduce((sum, i) => sum + i.quantity, 0);
    }
    updateInCart();
  });
})();
